import flow from 'lodash/fp/flow';
import { endOfMonth, subDays } from 'date-fns/fp';
import {
  AnalysisBounds,
  AnalysisCalendar,
  AnalysisErrorCodes,
  LastFailedAnalysisErrorCodes,
  AnalysesErrorMessages,
} from '@algoan/dashboard/model';
import { parseAnalysisCalendarMonth, setToEndOfDay, setToStartOfDay } from '@algoan/dashboard/utils';

export const getEndOfAnalysisFromCalendar = (calendar: AnalysisCalendar): Date => {
  return new Date(`${calendar.month}-${calendar.nbDays.toString().padStart(2, '0')}`);
};

export const getStartOfAnalysisFromCalendar = (calendar: AnalysisCalendar): Date => {
  const subNumberOfDays = subDays(calendar.nbDays - 1);
  return flow(parseAnalysisCalendarMonth, endOfMonth, subNumberOfDays, setToStartOfDay)(calendar.month);
};

export const formatAnalysisBounds = (
  calendar: AnalysisCalendar[],
  minWindowDate?: string,
): AnalysisBounds | undefined => {
  let earliestAnalysedDate: Date;
  let latestAnalysedDate: Date;

  if (calendar.length === 1 && minWindowDate) {
    earliestAnalysedDate = new Date(minWindowDate);
    latestAnalysedDate = new Date(minWindowDate);

    latestAnalysedDate.setDate(latestAnalysedDate.getDate() + calendar[0].nbDays - 1);
  } else {
    earliestAnalysedDate = getStartOfAnalysisFromCalendar(calendar[0]);
    latestAnalysedDate = getEndOfAnalysisFromCalendar(calendar[calendar.length - 1]);
  }

  return [setToStartOfDay(earliestAnalysedDate), setToEndOfDay(latestAnalysedDate)];
};

export const mapAnalysisErrorCodeToAnalysisMessage = (errorCode?: AnalysisErrorCodes): AnalysesErrorMessages => {
  switch (errorCode) {
    case AnalysisErrorCodes.CURRENCY_NOT_SUPPORTED:
      return AnalysesErrorMessages.CURRENCY_NOT_SUPPORTED;
    case AnalysisErrorCodes.USAGE_NOT_SUPPORTED:
      return AnalysesErrorMessages.USAGE_NOT_SUPPORTED;
    case AnalysisErrorCodes.NOT_ENOUGH_TRANSACTIONS:
      return AnalysesErrorMessages.NOT_ENOUGH_TRANSACTIONS;
    case AnalysisErrorCodes.NO_ANALYZABLE_ACCOUNT:
    case AnalysisErrorCodes.ACCOUNT_TYPE_NOT_SUPPORTED:
    case AnalysisErrorCodes.NO_CHECKING_ACCOUNT:
      return AnalysesErrorMessages.NO_CHECKING_ACCOUNT;
    case AnalysisErrorCodes.OWNERSHIP_NOT_SUPPORTED:
      return AnalysesErrorMessages.OWNERSHIP_NOT_SUPPORTED;
    case AnalysisErrorCodes.ZERO_INCOME:
      return AnalysesErrorMessages.ZERO_INCOME;
    case AnalysisErrorCodes.LOW_ACTIVITY_SCORE:
      return AnalysesErrorMessages.LOW_ACTIVITY_SCORE;
    default:
      return AnalysesErrorMessages.TECHNICAL_ERROR;
  }
};

export const mapLastFailedAnalysisErrorCodesToAnalysisMessage = (
  errorCode?: LastFailedAnalysisErrorCodes,
): AnalysesErrorMessages => {
  switch (errorCode) {
    case LastFailedAnalysisErrorCodes.NOT_ENOUGH_TRANSACTIONS:
      return AnalysesErrorMessages.NOT_ENOUGH_TRANSACTIONS;
    case LastFailedAnalysisErrorCodes.CURRENCY_NOT_SUPPORTED:
      return AnalysesErrorMessages.CURRENCY_NOT_SUPPORTED;
    case LastFailedAnalysisErrorCodes.USAGE_NOT_SUPPORTED:
      return AnalysesErrorMessages.USAGE_NOT_SUPPORTED;
    case LastFailedAnalysisErrorCodes.NO_CHECKING_ACCOUNT:
      return AnalysesErrorMessages.NO_CHECKING_ACCOUNT;
    case LastFailedAnalysisErrorCodes.OWNERSHIP_NOT_SUPPORTED:
      return AnalysesErrorMessages.OWNERSHIP_NOT_SUPPORTED;
    case LastFailedAnalysisErrorCodes.AGGREGATION_FAILED:
      return AnalysesErrorMessages.AGGREGATION_FAILED;
    case LastFailedAnalysisErrorCodes.LOW_ACTIVITY_SCORE:
      return AnalysesErrorMessages.LOW_ACTIVITY_SCORE;
    case LastFailedAnalysisErrorCodes.ZERO_INCOME:
      return AnalysesErrorMessages.ZERO_INCOME;
    default:
      return AnalysesErrorMessages.TECHNICAL_ERROR;
  }
};
